<template>
  <el-form
    :model="form"
    ref="form">
    <div class="row">
      <div class="col-4">
        <el-form-item
          :label="L10n('rfqid')"
          prop="rfqid"
          :rules="getValidationRule('required,minLength')"
          class="material-style">
          <el-input
            :value="form.rfqid"
            @input="form.rfqid = valueTrimLeft($event)"/>
        </el-form-item>
        <el-form-item
          :label="L10n('baxPO')"
          prop="baxPO"
          :rules="validateRulesBaxPO"
          class="material-style">
          <el-input
            :value="form.baxPO"
            @input="form.baxPO = valueTrimLeft($event)"/>
        </el-form-item>
        <el-form-item
          :label="L10n('customer-po')"
          prop="po"
          :rules="getValidationRule('required,minLength')"
          class="material-style">
          <el-input
            :value="form.po"
            @input="form.po = valueTrimLeft($event)"/>
        </el-form-item>
        <el-form-item
          :label="L10n('rfq-type')"
          prop="rfqTypeID"
          class="material-style"
        >
          <select-response
            :model.sync="form.rfqTypeID"
            :isLoading="dataSourceRfqType.loading"
            :data="dataSourceRfqType.records"
          ></select-response>
        </el-form-item>
      </div>
      <div class="col-8">
        <slot name="right-info"></slot>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <el-form-item
          :label="L10n('customer')"
          prop="customerCompanyID"
          :rules="getValidationRule('required')"
          class="material-style"
          :clearable="false"
        >
          <select-company
            :model.sync="form.customerCompanyID"
            @on-created="mixCreateCustomer"
            @on-change="mixChangeCustomerID"
          >
          </select-company>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item
          :label="L10n('customer-contact')"
          prop="customerContactID"
          class="material-style">
          <select-company-contact
            :model.sync="form.customerContactID"
            :companyID="form.customerCompanyID"
          ></select-company-contact>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item
          :label="L10n('customer-detail')"
          prop="customerCompanyDetailID"
          class="material-style">
          <select-company-detail
            withAutoSelect
            :model.sync="form.customerCompanyDetailID"
            :companyID="form.customerCompanyID"
          ></select-company-detail>
        </el-form-item>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <el-form-item
          :label="L10n('ship-to-company')"
          prop="shipToCompanyID"
          :rules="getValidationRule('required')"
          class="material-style"
        >
          <select-company
            :model.sync="form.shipToCompanyID"
            @on-change="(id) => mixChangeShipToID(id)"
            @on-created="mixCreateShipTo"
          >
          </select-company>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item
          :label="L10n('ship-to-company-contact')"
          prop="shipToCompanyContactID"
          class="material-style">
          <select-company-contact
            :model.sync="form.shipToCompanyContactID"
            :companyID="form.shipToCompanyID"
          ></select-company-contact>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item
          :label="L10n('ship-to-company-detail')"
          prop="shipToCompanyDetailID"
          class="material-style">
          <select-company-detail
            :model.sync="form.shipToCompanyDetailID"
            :companyID="form.shipToCompanyID"
          ></select-company-detail>
        </el-form-item>
      </div>
    </div>
    <div class="row">
      <div class="col-4">
        <el-form-item
          :label="L10n('ship-via-forwarder')"
          prop="shipViaForwarderID"
          class="material-style"
        >
          <select-forwarder
            :companyID="form.shipToCompanyID"
            :model.sync="form.shipViaForwarderID"
          ></select-forwarder>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item
          :label="L10n('vat')"
          prop="vatID"
          class="material-style">
          <select-response
            :model.sync="form.vatID"
            :fields="{id: 'id', title: 'country'}"
            :isLoading="dataSourceVAT.loading"
            :data="dataSourceVAT.records"
          ></select-response>
        </el-form-item>
      </div>
      <div class="col-4">
        <el-form-item
          :label="L10n('priority')"
          prop="priorityID"
          :rules="getValidationRule('required')"
          class="material-style">
          <select-response
            :model.sync="form.priorityID"
            :isLoading="dataSourcePriority.loading"
            :data="dataSourcePriority.records"
          ></select-response>
        </el-form-item>
      </div>
    </div>
  </el-form>
</template>

<script>
import Form from '@/components/Form/Form'
import { mixUpdateCustomer, mixUpdateShipTo } from '@/mixins/companies'
import { vatsDataSourceConverter } from '@/utils/shared'

export default {
  name: 'ButtonsPOForm',
  mixins: [ Form, mixUpdateCustomer, mixUpdateShipTo ],
  components: {
    'select-forwarder': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/SelectForwarder/Index.vue'),
    'select-company': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/SelectCompany/Index.vue'),
    'select-company-contact': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/SelectCompanyContact/Index.vue'),
    'select-company-detail': () => import(/* webpackChunkName: 'Shared' */ '@/components/Shared/SelectCompanyDetail/Index.vue')
  },
  props: {
    rulesBaxPO: {
      type: Array,
      required: true
    }
  },
  computed: {
    mixRelatedShipToFields () {
      return [
        {
          fieldName: 'shipToCompanyContactID',
          createField: 'contactID'
        },
        {
          fieldName: 'shipToCompanyDetailID',
          createField: 'companyDetailID'
        },
        {
          fieldName: 'shipViaForwarderID'
        }
      ]
    },
    validateRulesBaxPO () {
      return [...this.rulesBaxPO].concat(this.getValidationRule('required,minLength'))
    },
    dataSourceVAT () {
      return vatsDataSourceConverter(this.API('VATs/GetAllDynamic?Select=id,country&OrderBy=country&OrderBy=country'))
    },
    dataSourcePriority () {
      return this.API('Priorities/GetAllDynamic?select=id,title&orderBy=title')
    },
    dataSourceRfqType () {
      return this.API('RFQTypes/GetAllDynamic?select=id,title')
    }
  }
}
</script>
